import { isNonNullableObject } from "@knyt/artisan";
const PERCENTAGE_UNIT = "%";
/**
 * Represents a CSS Percentage value.
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/percentage | MDN CSS Length}
 */
export class CSSPercentage {
    static from(value) {
        if (value === undefined) {
            return undefined;
        }
        if (typeof value === "number") {
            return new CSSPercentage(value);
        }
        if (isCSSPercentage(value)) {
            return value;
        }
        if (value.endsWith(PERCENTAGE_UNIT)) {
            const ratioDecimal = parseFloat(value) / 100;
            return new CSSPercentage(ratioDecimal);
        }
        throw new TypeError(`CSSPercentage: Unrecognized value: ${value}`);
    }
    static isPercentageString(value) {
        return (typeof value === "string" &&
            value.endsWith(PERCENTAGE_UNIT) &&
            !isNaN(parseFloat(value)));
    }
    /**
     * The value of the percentage represented in pixels.
     */
    #ratioDecimal;
    constructor(ratioDecimal) {
        this.#ratioDecimal = ratioDecimal;
    }
    /**
     * Convert the percentage into a value using pixels.
     */
    toRatioDecimalValue() {
        return this.#ratioDecimal;
    }
    /**
     * Convert the percentage into a value using pixels.
     */
    toPercentageValue() {
        return this.#ratioDecimal * 100;
    }
    /**
     * Convert the percentage into a string with a "%" unit.
     */
    pct() {
        return `${this.toPercentageValue()}${PERCENTAGE_UNIT}`;
    }
    /**
     * Add this percentage to another percentage.
     */
    plus(other) {
        return this.#math(other, (a, b) => a + b);
    }
    /**
     * Subtract another percentage from this percentage.
     */
    minus(other) {
        return this.#math(other, (a, b) => a - b);
    }
    /**
     * Multiply this percentage by another percentage or a number.
     */
    times(other) {
        return this.#math(other, (a, b) => a * b);
    }
    /**
     * Get the maximum value between this percentage and another percentage.
     */
    max(other) {
        return this.#math(other, (a, b) => Math.max(a, b));
    }
    /**
     * Get the minimum value between this percentage and another percentage.
     */
    min(other) {
        return this.#math(other, (a, b) => Math.min(a, b));
    }
    /**
     * Divide this percentage by another percentage or a number.
     */
    dividedBy(other, rounding = "floor") {
        return this.#math(other, (pixelValueA, pixelValueB) => {
            return Math[rounding](pixelValueA / pixelValueB);
        });
    }
    /**
     * Enables automatic type coercion when using arithmetic operators.
     *
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/valueOf | MDN Primitive coercion}
     */
    valueOf() {
        return this.toRatioDecimalValue();
    }
    /**
     * Enables automatic type coercion into strings.
     *
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/toString | MDN Primitive coercion}
     */
    toString() {
        return this.pct();
    }
    toCSSString() {
        return this.pct();
    }
    /**
     * Perform a mathematical operation on this percentage and another percentage.
     */
    #math(other, operation) {
        return new CSSPercentage(operation(this.toRatioDecimalValue(), CSSPercentage.from(other).toRatioDecimalValue()));
    }
    get __KnytCSSPercentage() {
        return true;
    }
}
export function isCSSPercentage(value) {
    return (isNonNullableObject(value) &&
        "__KnytCSSPercentage" in value &&
        value.__KnytCSSPercentage === true);
}
